import React, { useState, useEffect } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";

function Settings() {
  const { t, i18n } = useTranslation();
  const shop =
    new URLSearchParams(window.location.search).get("shop") ||
    "victorgroups.myshopify.com";
  const [settings, setSettings] = useState({
    validationMode: "warn",
    poBoxHandling: "warn",
    costPerFailedShipment: "3",
    customWarning: "Please add village name to avoid delay.",
    language: "en",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    axios
      .get(
        `${
          process.env.NODE_ENV === "production"
            ? "https://fr-xfinder.vercel.app"
            : "http://localhost:3000"
        }/settings?shop=${shop}`
      )
      .then((response) => {
        if (response.data.settings) {
          setSettings(response.data.settings);
          i18n.changeLanguage(response.data.settings.language || "en");
        }
      })
      .catch((err) => {
        console.error("❌ Fetch settings error:", err.message);
        setError(t("settings.fetchError"));
      });
  }, [shop, t, i18n]);

  const handleChange = (field) => (value) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
    setError("");
    if (field === "language") {
      i18n.changeLanguage(value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await axios.post(
        `${
          process.env.NODE_ENV === "production"
            ? "https://fr-xfinder.vercel.app"
            : "http://localhost:3000"
        }/settings`,
        { ...settings, shop },
        {
          headers: { "Content-Type": "application/json" },
        }
      );
      alert(t("settings.saved"));
    } catch (error) {
      console.error("❌ Settings save error:", error.message);
      setError(t("settings.saveError"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-4 sm:p-6">
      <div className="max-w-md sm:max-w-lg mx-auto">
        <h3 className="text-lg sm:text-xl font-semibold text-gray-700 mb-3 sm:mb-4">
          {t("settings.title")}
        </h3>
        {error && (
          <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-3 sm:p-4 mb-4 sm:mb-6 rounded-md text-sm sm:text-base">
            {error}
          </div>
        )}
        <form
          onSubmit={handleSubmit}
          className="space-y-4 sm:space-y-6 bg-white p-4 sm:p-6 rounded-lg shadow-md"
        >
          <div>
            <label className="block text-sm font-medium text-gray-700">
              {t("settings.validationMode")}
            </label>
            <div className="mt-2 space-y-3">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="validationMode"
                  value="warn"
                  checked={settings.validationMode === "warn"}
                  onChange={() => handleChange("validationMode")("warn")}
                  className="mr-2 text-shopifyGreen focus:ring-shopifyGreen"
                />
                <span className="text-sm sm:text-base">
                  {t("settings.warnOnly")}
                </span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="validationMode"
                  value="require"
                  checked={settings.validationMode === "require"}
                  onChange={() => handleChange("validationMode")("require")}
                  className="mr-2 text-shopifyGreen focus:ring-shopifyGreen"
                />
                <span className="text-sm sm:text-base">
                  {t("settings.requireCorrection")}
                </span>
              </label>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              {t("settings.poBoxHandling")}
            </label>
            <div className="mt-2 space-y-3">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="poBoxHandling"
                  value="allow"
                  checked={settings.poBoxHandling === "allow"}
                  onChange={() => handleChange("poBoxHandling")("allow")}
                  className="mr-2 text-shopifyGreen focus:ring-shopifyGreen"
                />
                <span className="text-sm sm:text-base">
                  {t("settings.allow")}
                </span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="poBoxHandling"
                  value="warn"
                  checked={settings.poBoxHandling === "warn"}
                  onChange={() => handleChange("poBoxHandling")("warn")}
                  className="mr-2 text-shopifyGreen focus:ring-shopifyGreen"
                />
                <span className="text-sm sm:text-base">
                  {t("settings.warn")}
                </span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="poBoxHandling"
                  value="block"
                  checked={settings.poBoxHandling === "block"}
                  onChange={() => handleChange("poBoxHandling")("block")}
                  className="mr-2 text-shopifyGreen focus:ring-shopifyGreen"
                />
                <span className="text-sm sm:text-base">
                  {t("settings.block")}
                </span>
              </label>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              {t("settings.costPerFailedShipment")}
            </label>
            <input
              type="number"
              value={settings.costPerFailedShipment}
              onChange={(e) =>
                handleChange("costPerFailedShipment")(e.target.value)
              }
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm p-2 sm:p-3 focus:ring-shopifyGreen focus:border-shopifyGreen text-sm sm:text-base"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              {t("settings.customWarning")}
            </label>
            <textarea
              value={settings.customWarning}
              onChange={(e) => handleChange("customWarning")(e.target.value)}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm p-2 sm:p-3 focus:ring-shopifyGreen focus:border-shopifyGreen text-sm sm:text-base"
              rows="4"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              {t("settings.language")}
            </label>
            <select
              value={settings.language}
              onChange={(e) => handleChange("language")(e.target.value)}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm p-2 sm:p-3 focus:ring-shopifyGreen focus:border-shopifyGreen text-sm sm:text-base"
            >
              <option value="en">{t("settings.english")}</option>
              <option value="fr">{t("settings.french")}</option>
              <option value="es">{t("settings.spanish")}</option>
            </select>
          </div>
          <button
            type="submit"
            disabled={loading}
            className={`w-full bg-shopifyGreen text-white px-3 py-1.5 sm:px-4 sm:py-2 rounded-md hover:bg-shopifyGreen/90 font-semibold text-sm sm:text-base ${
              loading ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            {loading ? t("settings.saving") : t("settings.save")}
          </button>
        </form>
      </div>
    </div>
  );
}

export default Settings;
